/**
 * Opportunity Management Constants
 * Options for Services, Request Type, Referral Source, Status, and Regional Market
 */

// ============================================================================
// Service Options (for form dropdown and filters)
// ============================================================================

export const SERVICE_OPTIONS = [
  { value: 'software_solutions', label: 'Software Solutions' },
  { value: 'website_development', label: 'Website Development' },
  { value: 'branding', label: 'Branding' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other_services', label: 'Other Services' },
];

// Service options for checkbox dropdown (with IDs)
export const SERVICE_CHECKBOX_OPTIONS = [
  { id: 1, name: 'Software Solutions' },
  { id: 2, name: 'Website Development' },
  { id: 3, name: 'Branding' },
  { id: 4, name: 'Social Media' },
  { id: 5, name: 'Marketing' },
  { id: 6, name: 'Other Services' },
];

/**
 * Services that do NOT trigger a Service Details step.
 * When ALL selected services are in this set, the Service Details
 * step is skipped and only the Save action is available.
 */
export const SERVICES_WITHOUT_DETAILS: string[] = [
  'social_media',
  'marketing',
  'other_services',
];

// ============================================================================
// Service Deliverables Options
// ============================================================================

export const SERVICE_DELIVERABLES_OPTIONS = [
  { id: 1, name: 'BRD' },
  { id: 2, name: 'SRS' },
  { id: 3, name: 'User Flow Diagram' },
  { id: 4, name: 'APIs' },
];

/** Maps deliverable checkbox ID → API enum string */
export const SERVICE_DELIVERABLES_ENUM_MAP: Record<number, string> = {
  1: 'brd',
  2: 'srs',
  3: 'user_flow_diagram',
  4: 'apis',
};

/** Maps deliverable API enum string → checkbox ID */
export const SERVICE_DELIVERABLES_ID_MAP: Record<string, number> = {
  brd: 1,
  srs: 2,
  user_flow_diagram: 3,
  apis: 4,
};

// ============================================================================
// Request Type Options
// ============================================================================

export const REQUEST_TYPE_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'enhancement', label: 'Enhancement' },
];

// ============================================================================
// Referral Source Options
// ============================================================================

export const REFERRAL_SOURCE_OPTIONS = [
  { value: 'google', label: 'Google / Search Engine' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'friend', label: 'Friend / Colleague Referral' },
  { value: 'company', label: 'Company Website' },
  { value: 'online_advertisement', label: 'Online Advertisement' },
  { value: 'event', label: 'Event / Conference' },
  { value: 'sales_representative', label: 'Sales Representative' },
  { value: 'partner', label: 'Partner / Affiliate' },
  { value: 'other', label: 'Other' },
];

// ============================================================================
// Status Options
// ============================================================================

export const STATUS_OPTIONS = [
  { value: 'created', label: 'New' },
  { value: 'ready', label: 'Ready for Estimation' },
  { value: 'estimated', label: 'Estimated' },
  { value: 'sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

// All status options
export const ALL_STATUS_OPTIONS = [
  { value: 'created', label: 'New' },
  { value: 'ready', label: 'Ready for Estimation' },
  { value: 'estimated', label: 'Estimated' },
  { value: 'sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

// Editable status options (when status is "estimated" or beyond)
export const EDITABLE_STATUS_OPTIONS = [
  { value: 'sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

// ============================================================================
// Regional Market Options
// ============================================================================

export const REGIONAL_MARKET_OPTIONS = [
  { value: 'EG', label: 'Egypt' },
  { value: 'SA', label: 'KSA' },
];

// Allowed regional market country codes
export const REGIONAL_MARKET_CODES = ['EG', 'SA'];

// ============================================================================
// Filter Options (for table filtering)
// ============================================================================

export const REQUEST_TYPE_FILTER_OPTIONS = REQUEST_TYPE_OPTIONS;

export const SERVICE_FILTER_OPTIONS = SERVICE_OPTIONS;

export const STATUS_FILTER_OPTIONS = STATUS_OPTIONS;

// ============================================================================
// Type Exports
// ============================================================================

export type ServiceOption = (typeof SERVICE_OPTIONS)[number];
export type RequestTypeOption = (typeof REQUEST_TYPE_OPTIONS)[number];
export type ReferralSourceOption = (typeof REFERRAL_SOURCE_OPTIONS)[number];
export type StatusOption = (typeof STATUS_OPTIONS)[number];
export type RegionalMarketOption = (typeof REGIONAL_MARKET_OPTIONS)[number];
